import {
  CurrencyE67Enum,
  GuaranteeTypeEnum,
  OrderDetail
} from '@insurance/api/requests'
import { Card, Col, Divider, Form, Modal, Row } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CustomerStep } from './CustomerStep'
import { Details } from './Details'
import { GuaranteesInsuranceProvider } from './GuaranteesInsuranceContext'
import { GuaranteesInsuranceControls } from './GuaranteesInsuranceControls'
import { GuaranteesMediaStep } from './GuaranteesMediaStep'
import { GuaranteeStep } from './GuaranteeStep'
import { PartiesStep } from './PartiesStep'
import {
  formProps,
  getGuaranteesValuesFromStorage,
  removeGuaranteesValuesFromStorage,
  saveGuaranteesValuesToStorage,
  STEP
} from './utils'
import { iniMixtContractorValues } from '../PersonsFields'

type Props = {
  order?: OrderDetail
}

const forms = [
  <GuaranteeStep key={STEP.GUARANTEE} />,
  <PartiesStep key={STEP.PARTIES} />,
  <GuaranteesMediaStep key={STEP.MEDIA} />,
  <CustomerStep key={STEP.CUSTOMER} />
]

const {
  stepKey,
  priceKey,
  orderKey,
  guaranteeTypeProps,
  currencyProps,
  validFromProps,
  validToProps,
  guaranteedAmountProps,
  premiumProps,
  detailsProps
} = formProps()

const initValues = {
  [stepKey.name]: STEP.GUARANTEE,
  [priceKey.name]: undefined,
  [orderKey.name]: undefined,
  [validToProps.name]: undefined,
  [guaranteedAmountProps.name]: undefined,
  [premiumProps.name]: undefined,
  [detailsProps.name]: undefined,
  [guaranteeTypeProps.name]: GuaranteeTypeEnum.OFFER,
  [validFromProps.name]: dayjs(),
  [currencyProps.name]: CurrencyE67Enum.MDL,
  ...iniMixtContractorValues,
  set_beneficiary_data: true
}

export const GuaranteesInsurance = ({ order }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)

  const handleReadyToSaveData = () => setIsReady(true)

  const saveOnChangeValue = () => {
    const values = form.getFieldsValue(true)
    saveGuaranteesValuesToStorage(values, order?.id)
    return null
  }

  const formattedInitValues = useMemo(() => {
    const detailValues = order?.guarantees

    if (!detailValues) return initValues

    return {
      ...initValues,
      ...detailValues,
      ...(detailValues.valid_from && {
        valid_from: dayjs(detailValues.valid_from)
      }),
      ...(detailValues.valid_to && {
        valid_to: dayjs(detailValues.valid_to)
      }),
      // Both figures come back as strings (DecimalField); the number inputs
      // expect numbers.
      ...(detailValues.amount && {
        [guaranteedAmountProps.name]: Number(detailValues.amount)
      }),
      ...(detailValues.premium && {
        [premiumProps.name]: Number(detailValues.premium)
      }),
      [stepKey.name]: order?.stage || STEP.GUARANTEE,
      [orderKey.name]: order?.id
    }
  }, [order])

  useEffect(() => {
    const stored = getGuaranteesValuesFromStorage(order?.id)
    form.setFieldsValue({ ...formattedInitValues, ...stored })
  }, [form, formattedInitValues, order?.id])

  useEffect(() => {
    const stored = getGuaranteesValuesFromStorage(order?.id)
    if (!stored) return

    const modal = Modal.confirm({
      onCancel: () => {
        removeGuaranteesValuesFromStorage(order?.id)
        setIsReady(false)
        form.setFieldsValue(formattedInitValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => modal.destroy()
  }, [form, t, formattedInitValues, order?.id])

  return (
    <GuaranteesInsuranceProvider order={order}>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => <Card>{forms[form.getFieldValue(stepKey.name)]}</Card>}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <GuaranteesInsuranceControls
          onReadyToSaveData={handleReadyToSaveData}
        />

        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </GuaranteesInsuranceProvider>
  )
}
